import { lazy, Suspense, useEffect, useRef, useState, type ReactNode, type CSSProperties } from "react";
import { allMaterials, blueprints, modules, ships } from "@edcc/game-data";
import { shipArtFor } from "./lib/ship-art.ts";
import { donationWallets } from "./lib/donations.ts";
import { H3, HorizontalGallery, LetterHeading } from "./components/ScrollShowcase.tsx";
import { MasonryGallery } from "./components/MasonryGallery.tsx";
import { ReviewsMarquee } from "./components/ReviewsMarquee.tsx";
import { COMMUNITY_BUILD_CATEGORIES } from "./lib/community-builds.ts";
import { CommunityBuildsPage, PublicBuildPage } from "./pages/PublicBuildsPage.tsx";
import {
  FALLBACK_VERSION,
  formatMegabytes,
  formatReleaseDate,
  shortHash,
  useReleaseInfo,
} from "./lib/release.ts";

const ShipCataloguePage = lazy(() =>
  import("./pages/ShipCataloguePage.tsx").then((module) => ({ default: module.ShipCataloguePage })),
);
const ShipBuildStudioPage = lazy(() =>
  import("./pages/ShipBuildStudioPage.tsx").then((module) => ({ default: module.ShipBuildStudioPage })),
);
const ShipDetailPage = lazy(() =>
  import("./pages/ShipCataloguePage.tsx").then((module) => ({ default: module.ShipDetailPage })),
);
const ChangelogPage = lazy(() =>
  import("./pages/ChangelogPage.tsx").then((module) => ({ default: module.ChangelogPage })),
);
const RoadmapPage = lazy(() => import("./pages/RoadmapPage.tsx").then((module) => ({ default: module.RoadmapPage })));
const ReleasesPage = lazy(() =>
  import("./pages/ReleasesPage.tsx").then((module) => ({ default: module.ReleasesPage })),
);
const DevelopersPage = lazy(() =>
  import("./pages/DevelopersPage.tsx").then((module) => ({ default: module.DevelopersPage })),
);
const SupportPage = lazy(() => import("./pages/SupportPage.tsx").then((module) => ({ default: module.SupportPage })));
const AdminPage = lazy(() => import("./pages/AdminPage.tsx").then((module) => ({ default: module.AdminPage })));

const DOWNLOAD_HREF = "/downloads/EDCC-setup.exe";
const PUBLIC_API = "https://edcc.lol/v1";

const nav = [
  { label: "Ships", href: "/ships/", path: "/ships" },
  { label: "Community builds", href: "/builds/", path: "/builds" },
  { label: "Marketplace", href: "/marketplace/", path: "/marketplace" },
  { label: "Releases", href: "/releases/", path: "/releases" },
  { label: "Developers", href: "/developers/", path: "/developers" },
];
const homeNav = [
  { label: "Ships", href: "/ships/", path: "/ships" },
  { label: "Community builds", href: "/builds/", path: "/builds" },
  { label: "Marketplace", href: "/marketplace/", path: "/marketplace" },
];

function Mark() {
  return <img className="brand-mark" src="/edcc.svg" alt="" draggable={false} />;
}

function ReferenceIcon({ name, className }: { name: string; className?: string }) {
  return <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false"><use href={`/reference-icons.svg#i-${name}`} /></svg>;
}

function DownloadButton() {
  return <a className="download-button" href={DOWNLOAD_HREF} download><ReferenceIcon name="dl" /><span>Download for Windows</span></a>;
}

function GitHubStarButton() {
  return <a className="github-star-button" href="https://github.com/Mubloxi/EDCC" target="_blank" rel="noreferrer" aria-label="Star EDCC on GitHub">
    <span className="github-shine" aria-hidden="true" />
    <span className="github-mark" aria-hidden="true"><svg viewBox="0 0 438.549 438.549"><path d="M409.132 114.573c-19.608-33.596-46.205-60.194-79.798-79.8-33.598-19.607-70.277-29.408-110.063-29.408-39.781 0-76.472 9.804-110.063 29.408-33.596 19.605-60.192 46.204-79.8 79.8C9.803 148.168 0 184.854 0 224.63c0 47.78 13.94 90.745 41.827 128.906 27.884 38.164 63.906 64.572 108.063 79.227 5.14.954 8.945.283 11.419-1.996 2.475-2.282 3.711-5.14 3.711-8.562 0-.571-.049-5.708-.144-15.417a2549.81 2549.81 0 0 1-.144-25.406l-6.567 1.136c-4.187.767-9.469 1.092-15.846 1-6.374-.089-12.991-.757-19.842-1.999-6.854-1.231-13.229-4.086-19.13-8.559-5.898-4.473-10.085-10.328-12.56-17.556l-2.855-6.57c-1.903-4.374-4.899-9.233-8.992-14.559-4.093-5.331-8.232-8.945-12.419-10.848l-1.999-1.431c-1.332-.951-2.568-2.098-3.711-3.429-1.142-1.331-1.997-2.663-2.568-3.997-.572-1.335-.098-2.43 1.427-3.289 1.525-.859 4.281-1.276 8.28-1.276l5.708.853c3.807.763 8.516 3.042 14.133 6.851 5.614 3.806 10.229 8.754 13.846 14.842 4.38 7.806 9.657 13.754 15.846 17.847 6.184 4.093 12.419 6.136 18.699 6.136 6.28 0 11.704-.476 16.274-1.423 4.565-.952 8.848-2.383 12.847-4.285 1.713-12.758 6.377-22.559 13.988-29.41-10.848-1.14-20.601-2.857-29.264-5.14-8.658-2.286-17.605-5.996-26.835-11.14-9.235-5.137-16.896-11.516-22.985-19.126-6.09-7.614-11.088-17.61-14.987-29.979-3.901-12.374-5.852-26.648-5.852-42.826 0-23.035 7.52-42.637 22.557-58.817-7.044-17.318-6.379-36.732 1.997-58.24 5.52-1.715 13.706-.428 24.554 3.853 10.85 4.283 18.794 7.952 23.84 10.994 5.046 3.041 9.089 5.618 12.135 7.708 17.705-4.947 35.976-7.421 54.818-7.421s37.117 2.474 54.823 7.421l10.849-6.849c7.419-4.57 16.18-8.758 26.262-12.565 10.088-3.805 17.802-4.853 23.134-3.138 8.562 21.509 9.325 40.922 2.279 58.24 15.036 16.18 22.559 35.787 22.559 58.817 0 16.178-1.958 30.497-5.853 42.966-3.9 12.471-8.941 22.457-15.125 29.979-6.191 7.521-13.901 13.85-23.131 18.986-9.232 5.14-18.182 8.85-26.84 11.136-8.662 2.286-18.415 4.004-29.263 5.146 9.894 8.562 14.842 22.077 14.842 40.539v60.237c0 3.422 1.19 6.279 3.572 8.562 2.379 2.279 6.136 2.95 11.276 1.995 44.163-14.653 80.185-41.062 108.068-79.226 27.88-38.161 41.825-81.126 41.825-128.906-.01-39.771-9.818-76.454-29.414-110.049z" /></svg></span>
    <span>Star on GitHub</span><svg className="github-star-mark" viewBox="0 0 24 24" aria-hidden="true"><path d="m12 2.8 2.8 5.7 6.3.9-4.55 4.43 1.08 6.27L12 17.14l-5.63 2.96 1.08-6.27L2.9 9.4l6.3-.91L12 2.8z" /></svg>
  </a>;
}

function DiscordButton() {
  return <a className="discord-play-button" href="https://discord.gg/jSmytGys7U" target="_blank" rel="noreferrer">
    <svg viewBox="0 -28.5 256 256" className="discord-mark" aria-hidden="true"><path d="M216.856339,16.5966031 C200.285002,8.84328665 182.566144,3.2084988 164.041564,0 C161.766523,4.11318106 159.108624,9.64549908 157.276099,14.0464379 C137.583995,11.0849896 118.072967,11.0849896 98.7430163,14.0464379 C96.9108417,9.64549908 94.1925838,4.11318106 91.8971895,0 C73.3526068,3.2084988 55.6133949,8.86399117 39.0420583,16.6376612 C5.61752293,67.146514 -3.4433191,116.400813 1.08711069,164.955721 C23.2560196,181.510915 44.7403634,191.567697 65.8621325,198.148576 C71.0772152,190.971126 75.7283628,183.341335 79.7352139,175.300261 C72.104019,172.400575 64.794972,168.822202 57.8887866,164.667963 C59.720961,163.310589 61.5131304,161.891452 63.2445898,160.431257 C105.36741,180.133187 151.134928,180.133187 192.754523,160.431257 C194.506336,161.891452 196.298154,163.310589 198.110326,164.667963 C191.183787,168.842556 183.854737,172.420929 176.223542,175.320965 C180.230393,183.341335 184.861538,190.991831 190.096624,198.16893 C211.238746,191.588051 232.743023,181.531619 254.911949,164.955721 C260.227747,108.668201 245.831087,59.8662432 216.856339,16.5966031 Z M85.4738752,135.09489 C72.8290281,135.09489 62.4592217,123.290155 62.4592217,108.914901 C62.4592217,94.539647 72.607595,82.714558 85.4738752,82.714558 C98.3405064,82.714558 108.709962,94.518942 108.488529,108.914901 C108.508529,123.290155 98.3405064,135.09489 85.4738752,135.09489 Z M170.525237,135.09489 C157.88039,135.09489 147.510806,123.290155 147.510806,108.914901 C147.510806,94.539647 157.658606,82.714558 170.525237,82.714558 C183.391868,82.714558 193.761324,94.518942 193.539891,108.914901 C193.539891,123.290155 183.391868,135.09489 170.525237,135.09489 Z" /></svg>
    <span className="discord-texts"><small>JOIN THE</small><strong>DISCORD</strong></span>
  </a>;
}

function Spinner(props: { size?: number }) {
  const size = props.size ?? 28;
  return (
    <img
      className="spinner"
      src="/ed-icons/loading.svg"
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
  );
}

function KofiWidget() {
  return <a className="kofi-button" href="https://ko-fi.com/mubloxi" target="_blank" rel="noreferrer" aria-label="Support EDCC on Ko-fi"><img src="https://storage.ko-fi.com/cdn/kofi6.png?v=6" alt="Support EDCC on Ko-fi" /></a>;
}
function SiteShell({ children, active }: { children: ReactNode; active: string }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [copiedDonation, setCopiedDonation] = useState("");
  const visibleNav = active === "/" ? homeNav : nav;

  useEffect(() => {
    if (active === "/admin" || navigator.doNotTrack === "1" || window.localStorage.getItem("edcc.site-metrics.opt-out") === "1") return;
    const page = window.location.pathname.split(/[?#]/, 1)[0] || "/";
    if (page !== "/build/" && !page.startsWith("/build/") && !page.startsWith("/admin")) {
      void fetch("/analytics/event", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type: "pageview", path: page }), keepalive: true }).catch(() => {});
    }
    const onClick = (event: MouseEvent) => {
      if (window.localStorage.getItem("edcc.site-metrics.opt-out") === "1") return;
      const target = event.target instanceof Element ? event.target.closest("a[href],button") : null;
      if (!(target instanceof HTMLElement) || target.closest("form")) return;
      const rawHref = target instanceof HTMLAnchorElement ? target.href : "";
      let destination = "";
      if (rawHref) {
        try {
          const url = new URL(rawHref);
          destination = url.origin === window.location.origin ? url.pathname : url.origin;
        } catch { return; }
      }
      const label = (target.getAttribute("aria-label") || target.textContent || "").replace(/\s+/g, " ").trim().slice(0, 64);
      void fetch("/analytics/event", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ type: "click", path: page, label, destination }), keepalive: true }).catch(() => {});
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [active]);

  async function copyDonation(walletKey: string, address: string) {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedDonation(walletKey);
      window.setTimeout(() => setCopiedDonation((value) => value === walletKey ? "" : value), 1800);
    } catch {
      setCopiedDonation("unavailable");
    }
  }

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const nodes = document.querySelectorAll<HTMLElement>(".wow");
    if (!("IntersectionObserver" in window)) {
      nodes.forEach((node) => node.classList.add("is-in"));
      return;
    }
    const timers = new Set<number>();
    const observer = new IntersectionObserver((entries) => {
      const entering = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      for (const [index, entry] of entering.entries()) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        const node = entry.target as HTMLElement;
        // A short local stagger gives cards a reading order without delaying
        // content farther down the page until its own section enters view.
        const delay = Math.min(index, 3) * 55;
        const timer = window.setTimeout(() => {
          node.classList.add("is-in");
          timers.delete(timer);
        }, delay);
        timers.add(timer);
      }
    }, { rootMargin: "0px 0px -12% 0px", threshold: 0.12 });
    nodes.forEach((node) => observer.observe(node));
    return () => {
      observer.disconnect();
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [active]);

  return (
    <div className={`site-frame${active === "/" ? " site-frame-home" : ""}${active === "/admin" ? " site-frame-admin" : ""}`}>
      {active !== "/admin" ? <><a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="site-header">
        <div className="header-inner">
          <a className="brand" href="/" aria-label="EDCC home">
            <Mark />
            <span className="brand-title">EDCC</span>
          </a>
          <button
            type="button"
            className="nav-toggle"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            <span />
            <span />
            <span />
          </button>
          <nav className={`site-nav${menuOpen ? " is-open" : ""}`} aria-label="Main navigation">
            {visibleNav.map((item) => (
              <a
                key={item.path}
                href={item.href}
                aria-current={active === item.path ? "page" : undefined}
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}
            <a className="nav-download" href={DOWNLOAD_HREF} download>
              Download
            </a>
          </nav>
        </div>
      </header></> : null}

      <main id="main">{children}</main>

      {active !== "/admin" ? <footer className="site-footer">
        <div className="container">
          <div className="footer-widgets">
            <div className="footer-widget">
              <a className="footer-brand" href="/">
                <Mark />
                <span>EDCC</span>
              </a>
              <p>
                The all-in-one Elite Dangerous companion for Windows. Journal, fleet, builds, engineering, materials,
                trade and music in one local window.
              </p>
            </div>
            <div className="footer-widget">
              <h4>Product</h4>
              <div className="footer-links">
                <a href="/#features">Features</a>
                <a href="/ships/">Ship catalogue</a>
                <a href="/releases/">Releases</a>
                <a href={DOWNLOAD_HREF} download>
                  Download
                </a>
              </div>
            </div>
            <div className="footer-widget">
              <h4>Project</h4>
              <div className="footer-links">
                <a href="/roadmap/">Roadmap</a>
                <a href="/changelog/">Changelog</a>
                <a href="/developers/">Developers</a>
                <a href="/marketplace/">Marketplace</a>
                <a href="/developers/#community-goals">Community Goals API</a>
              </div>
              <div className="footer-social-actions"><GitHubStarButton /><DiscordButton /></div>
            </div>
            <div className="footer-widget">
              <h4>The fine print</h4>
              <div className="footer-links">
                <a href="/terms/">Terms of Service</a>
              </div>
            </div>
          </div>
          <section className="footer-donations" aria-labelledby="footer-donations-title">
            <div className="footer-donations-main"><div className="footer-donations-heading"><div><p className="eyebrow">Optional support</p><h3 id="footer-donations-title">Fuel the next jump.</h3><p>EDCC stays free. Chip in if you want to help keep it moving.</p></div><div className="footer-donations-actions"><KofiWidget /><a href="/support/">Other ways to support</a></div></div></div>
            <details className="footer-other-support"><summary>Crypto addresses and network details</summary><div className="footer-wallet-grid">{donationWallets.map((wallet) => <div className="footer-wallet" key={wallet.key}>
              <div className="footer-wallet-label"><img src={wallet.icon} alt="" /><span><strong>{wallet.key}</strong><small>{wallet.network}</small></span></div>
              <code title={wallet.address}>{wallet.address}</code>
              <button className="footer-copy-button" type="button" onClick={() => void copyDonation(wallet.key, wallet.address)}>{copiedDonation === wallet.key ? "Copied" : copiedDonation === "unavailable" ? "Clipboard unavailable" : "Copy"}</button>
            </div>)}</div><p className="footer-donation-note" aria-live="polite">{copiedDonation && copiedDonation !== "unavailable" ? `${copiedDonation} address copied.` : copiedDonation === "unavailable" ? "Clipboard unavailable in this browser." : "Confirm the asset and network before sending. EDCC cannot reverse or recover transfers."}</p></details>
          </section>
          <div className="footer-bottom">
            <p>
              Unofficial fan project. Not affiliated with Frontier Developments. Elite Dangerous is a trademark of
              Frontier Developments plc.
            </p>
            <p className="footer-creator">Created by CMDR MUBLOXI · Discord: mu_bloxi</p>
            <span className="mono">
              v{FALLBACK_VERSION} / Windows / local-first
            </span>
          </div>
          <div className="footer-wordmark" aria-hidden="true">EDCC</div>
        </div>
      </footer> : null}
    </div>
  );
}

const features = [
  {
    icon: "book",
    title: "The journal, made readable",
    detail:
      "Jumps, scans, missions, combat, rank changes and activity, read from the journals Elite already writes. No memory reading, no input automation.",
    href: "/#inside",
  },
  {
    icon: "games",
    title: "Fleet, loadouts and jump range",
    detail:
      "The last recorded loadout for every hull, with official ship art, module groups, engineering and unladen or laden jump range.",
    href: "/ships/",
  },
  {
    icon: "puzzle_piece",
    title: "Builds and engineering plans",
    detail:
      "Plan slot by slot, set a blueprint, grade and experimental effect, and see the material bill before you fly to the engineer.",
    href: "/#inside",
  },
  {
    icon: "shield",
    title: "Local by design",
    detail:
      "The core binds to loopback and the database lives in your own EDCC folder. The desktop app has no telemetry and keeps commander data local.",
    href: "/terms/",
  },
];

const inside = [
  {
    icon: "box",
    title: "Materials and collection",
    detail: "Raw, manufactured and encoded holdings with capacity, grades and written collection notes.",
  },
  {
    icon: "search",
    title: "Exploration and codex",
    detail: "Scans, first discoveries, Codex entries, bookmarks, the route planner and galaxy views.",
  },
  {
    icon: "flash",
    title: "Trade and ship sellers",
    detail: "Spansh routes from your station and nearby shipyards, with an honest note when a source cannot answer.",
  },
  {
    icon: "desktop",
    title: "Now Playing",
    detail: "Spotify and YouTube Music detected through Windows, real cover art when the session exposes it.",
  },
  {
    icon: "book",
    title: "Voice and copilot",
    detail: "Local Windows speech for narration and push-to-talk, plus a copilot you configure yourself.",
  },
  {
    icon: "list_bar",
    title: "GalNet and goals",
    detail: "GalNet news in the app, and Community Goals from Inara or the public EDCC feed.",
  },
  {
    icon: "cube",
    title: "Ship catalogue",
    detail: "Hull specifications and default outfitting from the verified community data that ships with EDCC.",
  },
  {
    icon: "cloud_arrow_down",
    title: "Full history import",
    detail: "Replay every journal on disk once, in order, without duplicating the history already ingested.",
  },
];

const testimonials = [
  { quote: "The Kumo Crew reviewed the route planner. Carry extra fuel. And a lot of extra caution.", name: "Archon Delaine", role: "Powerplay leader · fictional quote", image: "/character-portraits/Archon-Delaine-portrait.png", stars: 5 },
  { quote: "The shipyard supplied a tasteful red theme. I have questions about the invoice.", name: "Arissa Lavigny-Duval", role: "Powerplay leader · fictional quote", image: "/character-portraits/Arissa-Lavigny-Duval-portrait.png", stars: 5 },
  { quote: "The Alliance says the journal was already on my computer. Typical independent thinking.", name: "Edmund Mahon", role: "Powerplay leader · fictional quote", image: "/character-portraits/Edmund-Mahon-portrait.png", stars: 5 },
  { quote: "EDCC keeps the data local. I remain suspicious, but mildly impressed.", name: "Felicia Winters", role: "Powerplay leader · fictional quote", image: "/character-portraits/Felicia-Winters-portrait.png", stars: 5 },
  { quote: "The dashboard is efficient. Accounting is still calculating what that adjective is worth.", name: "Li Yong-Rui", role: "Powerplay leader · fictional quote", image: "/character-portraits/Li-Yong-Rui-portrait.png", stars: 5 },
  { quote: "An open route plan is useful. I will meditate on that after this next jump.", name: "Pranav Antal", role: "Powerplay leader · fictional quote", image: "/character-portraits/Pranav-Antal-portrait.png", stars: 5 },
  { quote: "This report is concise. I have directed the Senate to take notes and shorten its meetings.", name: "Zachary Hudson", role: "Powerplay leader · fictional quote", image: "/character-portraits/Zachary-Hudson-portrait.png", stars: 5 },
  { quote: "The outfitting list is acceptable. Now, where did my assistant put the list of assistants?", name: "Zemina Torval", role: "Powerplay leader · fictional quote", image: "/character-portraits/Zemina-Torval-portrait.png", stars: 5 },
  { quote: "Local data, clean ship records. Our legal team has no further questions at this time.", name: "Core Dynamics", role: "Manufacturer · fictional quote", stars: 5 },
  { quote: "The next system is marked. I still press the jump key myself. Five stars, pending review.", name: "CMDR One More Jump", role: "Pilots’ Federation · fictional quote", image: "/engineer-images/the-sarge.webp", stars: 5 },
  { quote: "I asked the Alliance for a second opinion. They said the journal was already on my computer. Typical independent thinking.", name: "Felicity Farseer", role: "Engineer · fictional quote", image: "/engineer-images/felicity-farseer.webp", stars: 5 },
  { quote: "The Dweller recommends keeping your data close and your secrets closer. He has not reviewed this product. Probably.", name: "The Dweller", role: "Engineer · fictional quote", image: "/engineer-images/the-dweller.webp", stars: 5 },
  { quote: "I asked for a better way to track materials. Turner said the numbers were already in the journal. Annoyingly, he was right.", name: "Bill Turner", role: "Engineer · fictional quote", image: "/engineer-images/bill-turner.webp", stars: 5 },
  { quote: "I can see the route, the hull and the modules. I still have to fit the ship myself. Standards have slipped.", name: "Selene Jean", role: "Engineer · fictional quote", image: "/engineer-images/selene-jean.webp", stars: 1 },
  { quote: "The next system is marked. The route is planned. I still press the jump key myself. Five stars, pending review.", name: "Tod ‘The Sarge’ McQuinn", role: "Engineer · fictional quote", image: "/engineer-images/the-sarge.webp", stars: 5 },
];

const mixedTestimonials = [...testimonials];
for (let index = mixedTestimonials.length - 1; index > 0; index -= 1) {
  const swap = Math.floor(Math.random() * (index + 1));
  [mixedTestimonials[index], mixedTestimonials[swap]] = [mixedTestimonials[swap]!, mixedTestimonials[index]!];
}
const faq = [
  {
    question: "Is EDCC trying to replace Inara or EDSY?",
    answer:
      "No. EDCC is the local companion in between: journal, fleet, builds, materials, trade and the small checks you make every session. Inara still has the social side and EDSY still has the deepest outfitting maths.",
  },
  {
    question: "Does EDCC need an account?",
    answer:
      "No. The app has no sign-up, cloud account or telemetry. The public website collects optional anonymous aggregate traffic metrics; it never receives commander or journal data. The journal agent reads the files Elite writes. Inara is optional and only used for the features you connect.",
  },
  {
    question: "Where does my data go?",
    answer:
      "Into a folder on your own disk. The database, settings and logs stay there. Delete the folder and they are gone. Network lookups only happen for the services you configure, such as EDSM, Spansh, Inara or an AI provider.",
  },
  {
    question: "Does EDCC show ARX?",
    answer:
      "Not currently. ARX requires Frontier account authorization and is not present in journals or Inara profile data. Automatic detection is planned; manual entry is not.",
  },
  {
    question: "Is EDCC affiliated with Frontier?",
    answer:
      "No. It is an unofficial fan project. Elite Dangerous is a trademark of Frontier Developments plc, and EDCC does not ship game files, credentials or another app's API key.",
  },
];

const appScreens: Array<{ src: string; alt: string; title: string; detail: string; width: number; height: number }> = [
  { src: "/app-shots/dashboard-current.png", alt: "Current EDCC dashboard capture showing credits, ships, materials, activity, exploration, combat, system briefing and charts", title: "Dashboard, with the stats", detail: "A fresh capture from the running app; all values remain inside the screenshot.", width: 1496, height: 959 },
  { src: "/app-shots/ship-catalogue.png", alt: "EDCC ship catalogue with verified ship data", title: "Ship catalogue", detail: `${ships.length} verified hulls, with published values and real module data.`, width: 1496, height: 959 },
  { src: "/app-shots/materials.png", alt: "EDCC materials page showing journal-synced holdings and collection notes", title: "Materials", detail: "Journal-synced holdings and collection notes.", width: 1496, height: 959 },
  { src: "/app-shots/integrations.png", alt: "EDCC integrations page showing Inara, EDSM, Spansh, and beta community tools", title: "Connected on your terms", detail: "Optional services, clear status, and honest beta labels.", width: 1496, height: 959 },
];

function AppScreenshotGallery() {
  return <HorizontalGallery>{appScreens.map((shot, index) => <figure className="scroll-gallery-slide" key={shot.src}><a href={shot.src} target="_blank" rel="noreferrer" aria-label={`Open full-size screenshot: ${shot.title}`}><img src={shot.src} alt={shot.alt} loading={index < 2 ? "eager" : "lazy"} width={shot.width} height={shot.height} style={{ aspectRatio: `${shot.width} / ${shot.height}` }} draggable={false} /></a><figcaption><strong>{shot.title}</strong><span>{shot.detail}</span></figcaption></figure>)}</HorizontalGallery>;
}

function HomeMetrics() {
  const root = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const node = root.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { threshold: .3 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setProgress(1); return; }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 1600);
      setProgress(1 - Math.pow(1 - t, 3));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [visible]);
  const values = [ships.length, modules.length, allMaterials.length, blueprints.length];
  const labels = ["verified ship hulls", "module specifications", "tracked materials", "engineering blueprints"];
  return <section ref={root} className="home-metrics" aria-label="EDCC game data">
    {values.map((value, index) => <article key={labels[index]}><strong className={index % 2 === 0 ? "metric-cyan" : "metric-orange"}>{Math.round(value * progress).toLocaleString("en-US")}</strong><span><i aria-hidden="true"><ReferenceIcon name={index === 0 ? "games" : index === 1 ? "list_bar" : index === 2 ? "box" : "puzzle_piece"} /></i> {labels[index]}</span></article>)}
  </section>;
}

function AnalyticsPrivacyToggle() {
  const [optedOut, setOptedOut] = useState(() => window.localStorage.getItem("edcc.site-metrics.opt-out") === "1");
  function toggle() {
    const next = !optedOut;
    window.localStorage.setItem("edcc.site-metrics.opt-out", next ? "1" : "0");
    setOptedOut(next);
  }
  return <button className="analytics-opt-out" type="button" onClick={toggle}>{optedOut ? "Turn anonymous site metrics on" : "Turn anonymous site metrics off"}</button>;
}

function HeroShipRibbon({ items }: { items: Array<{ id: string; img: string; title: string; alt: string }> }) {
  const ribbon = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ribbon.current;
    if (!root) return;

    const cards = Array.from(root.querySelectorAll<HTMLAnchorElement>(".ship-ribbon-track a"));
    const updateFocus = () => {
      const bounds = root.getBoundingClientRect();
      const center = bounds.left + bounds.width / 2;
      for (const card of cards) {
        const cardBounds = card.getBoundingClientRect();
        const distance = Math.min(1, Math.abs(cardBounds.left + cardBounds.width / 2 - center) / (bounds.width / 2));
        const scale = 1.48 - distance * 0.78;
        const offset = -20 + distance * 36;
        card.style.setProperty("--ship-focus-scale", scale.toFixed(3));
        card.style.setProperty("--ship-focus-y", `${offset.toFixed(1)}px`);
        card.style.zIndex = String(Math.round(scale * 100));
      }
    };

    updateFocus();
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    let lastUpdate = 0;
    const tick = (time: number) => {
      if (document.visibilityState === "visible" && time - lastUpdate >= 32) {
        updateFocus();
        lastUpdate = time;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    window.addEventListener("resize", updateFocus);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateFocus);
    };
  }, []);

  return <div ref={ribbon} className="ship-ribbon" role="region" aria-label="All Elite Dangerous ships in the catalogue">
    <div className="ship-ribbon-track">{[0, 1].map((copy) => <div className="ship-ribbon-set" key={copy} aria-hidden={copy === 1}>{items.map((ship, index) => <a key={ship.id} href={copy === 0 ? `/ships/${ship.id}/` : undefined} tabIndex={copy === 1 ? -1 : undefined}>
      <img src={ship.img} alt={copy === 1 ? "" : ship.alt} loading={copy === 0 && index < 8 ? "eager" : "lazy"} />
      <span>{ship.title}</span>
    </a>)}</div>)}</div>
  </div>;
}

function ThemePacksSection() {
  const [preview, setPreview] = useState<"hangar" | "amoled">("hangar");
  const previews = {
    hangar: { src: "/app-shots/dashboard-current.png", label: "Hangar · orange standard", alt: "Fresh direct capture of EDCC running with its standard orange appearance" },
    amoled: { src: "/app-shots/dashboard-amoled.png", label: "AMOLED · true black", alt: "Fresh direct capture of EDCC running with its AMOLED appearance" },
  } as const;
  const items = [
    { icon: "desktop", title: "Animated GPU wallpapers", detail: "The local WebGL nebula animates behind the cockpit panels, pauses while EDCC is hidden and respects reduced-motion settings. Animated GIF wallpapers are supported too." },
    { icon: "puzzle_piece", title: "Custom icon packs", detail: "Install safe SVG icons for the sidebar, then export them with your background and appearance controls." },
    { icon: "cloud_arrow_down", title: "Share what you make", detail: "Import or export one EDCC theme file with wallpapers, colors, transparency, blur, icons and sidebar layout." },
  ];
  return <section className="ud-section theme-packs" aria-labelledby="theme-packs-title">
    <div className="container">
      <div className="ud-section-title wow">
        <p className="eyebrow">Make the cockpit yours</p>
        <h2 id="theme-packs-title" className="accent-heading">Full theme packs</h2>
        <p>Set a backdrop, tune its blur and shade, switch on the GPU shader and choose your sidebar icons. Export the set as one file and share it without a fork.</p>
      </div>
      <div className="theme-preview-demo wow">
        <div className="theme-preview-options" role="group" aria-label="Theme preview">
          {(["amoled", "hangar"] as const).map((theme) => <button type="button" key={theme} aria-pressed={preview === theme} className={preview === theme ? "selected" : ""} onClick={() => setPreview(theme)}><span className={`theme-preview-swatch ${theme}`} />{theme === "amoled" ? "AMOLED" : "Hangar orange"}</button>)}
        </div>
        <figure className={`theme-preview-window ${preview}`} aria-label={`${preview} appearance screenshot`}>
          <img src={previews[preview].src} alt={previews[preview].alt} loading="lazy" width="1496" height="959" />
          <figcaption>{previews[preview].label}</figcaption>
        </figure>
        <p className="theme-preview-caption">Direct captures of EDCC running with each appearance selected. No filters are applied to the images.</p>
      </div>
      <div className="row">{items.map((item) => <div className="col-xl-4 col-lg-4 col-sm-6" key={item.title}><article className="feature-card theme-pack-card wow"><div className="ud-feature-icon"><ReferenceIcon name={item.icon} /></div><h3>{item.title}</h3><p>{item.detail}</p></article></div>)}</div>
      <p className="theme-pack-note"><a href="/themes/">Read the theme and icon pack guide</a></p>
      <p className="theme-pack-note">Wallpaper video uploads are not supported. Animated GIFs and the built-in GPU wallpaper work locally; video playback support is still planned.</p>
    </div>
  </section>;
}

function ThemesGuidePage() {
  const packs = [
    { name: "Hangar", description: "EDCC's standard dark cockpit with warm orange accents.", href: "/theme-packs/hangar.json", className: "hangar" },
    { name: "AMOLED", description: "True-black surfaces for OLED displays, with readable text and restrained orange cues.", href: "/theme-packs/amoled.json", className: "amoled" },
  ];
  return <section className="ud-section theme-guide-page"><div className="container">
    <div className="ud-section-title"><p className="eyebrow">EDCC appearance guide</p><h1 className="accent-heading">Build a cockpit that feels like yours.</h1><p>Start with a preset, tune the controls in Settings → Appearance, then export one theme file to move it to another install.</p></div>
    <div className="theme-guide-presets">{packs.map((pack) => <article className={`theme-guide-preset ${pack.className}`} key={pack.name}><span className="theme-guide-chip" /><h2>{pack.name}</h2><p>{pack.description}</p><a href={pack.href} download>Download preset</a></article>)}</div>
    <div className="theme-guide-steps"><article><span>01</span><h2>Set a wallpaper</h2><p>Choose a PNG, JPEG, WebP or animated GIF up to 15 MB for local use. Marketplace submissions have a smaller 1.5 MB wallpaper limit. The file is saved in this browser profile. The built-in animated nebula is drawn locally by WebGL and pauses when EDCC is hidden.</p></article><article><span>02</span><h2>Tune the surfaces</h2><p>Picture blur and shade affect the wallpaper. Panel transparency blends the panel's color with the background; text stays fully opaque and readable. Panel blur softens cards, buttons and the sidebar.</p></article><article><span>03</span><h2>Choose colors</h2><p>The color studio adjusts the app background, panels, main and secondary text, accent, buttons and progress bars. Color values stay local; reset appearance to return to Hangar defaults.</p></article><article><span>04</span><h2>Install icon packs</h2><p>Import a JSON object mapping sidebar route names to simple SVG icons. EDCC strips scripts, external links, styles, filters and unsupported SVG elements before applying them.</p></article></div>
    <section className="theme-guide-share"><p className="eyebrow">Keep it portable</p><h2>Export. Share the file. Import it anywhere.</h2><p>Theme packs contain appearance values, the optional background image or GIF, safe SVG sidebar icons and sidebar layout. They do not upload your commander data.</p><a className="ud-main-btn" href="/#screenshots">See the app</a></section>
  </div></section>;
}

interface MarketplaceItem { id: string; createdAt: string; title: string; description: string; category: "theme" | "icon-pack" | "wallpaper"; tags: string[]; authorName: string | null; previewUrl: string }
function MarketplacePage() {
  const [items, setItems] = useState<MarketplaceItem[]>([]);
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState("Loading approved community packs…");
  const [loading, setLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const load = async (cursor?: string, append = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "36" });
      if (category) params.set("category", category);
      if (query.trim()) params.set("q", query.trim());
      if (cursor) params.set("cursor", cursor);
      const response = await fetch(`${PUBLIC_API}/themes?${params}`, { headers: { accept: "application/json" } });
      if (!response.ok) {
        if (response.status === 404) throw new Error("The marketplace API route is not deployed (HTTP 404). Listings are unavailable until the public service is restored.");
        throw new Error(`Marketplace service returned ${response.status}.`);
      }
      const body = await response.json() as { items?: MarketplaceItem[]; nextCursor?: string | null };
      setItems((previous) => append ? [...previous, ...(body.items ?? [])] : (Array.isArray(body.items) ? body.items : []));
      setNextCursor(body.nextCursor ?? null);
      setMessage(body.items?.length ? "" : "No approved listings yet. New submissions appear here after review.");
    } catch (error) {
      setItems([]);
      setMessage(error instanceof TypeError
        ? "The marketplace API could not be reached. Check the public service and try again."
        : error instanceof Error ? error.message : "Marketplace is temporarily unavailable.");
    } finally { setLoading(false); }
  };
  useEffect(() => { setItems([]); setNextCursor(null); void load(); }, [category]);
  const downloadPack = async (item: MarketplaceItem) => {
    try {
      const response = await fetch(`${PUBLIC_API}/themes/${encodeURIComponent(item.id)}`);
      if (!response.ok) throw new Error("That pack is no longer available.");
      const result = await response.json() as { themePack: unknown };
      const url = URL.createObjectURL(new Blob([JSON.stringify(result.themePack, null, 2)], { type: "application/json" }));
      const anchor = document.createElement("a"); anchor.href = url; anchor.download = `${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-") || "edcc-pack"}.json`; anchor.click(); URL.revokeObjectURL(url);
    } catch (error) { setMessage(error instanceof Error ? error.message : "Could not download this pack."); }
  };
  return <section className="ud-section marketplace-page"><div className="container">
    <div className="ud-section-title"><h1 className="accent-heading">Appearance packs</h1><p>Themes, icons and wallpapers for EDCC.</p></div>
    <div className="marketplace-toolbar"><label><span>Search packs</span><input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void load(); }} placeholder="AMOLED, exploration…" /></label><label><span>Category</span><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="">Everything</option><option value="theme">Themes</option><option value="icon-pack">Icon packs</option><option value="wallpaper">Wallpapers</option></select></label><button className="ud-main-btn" type="button" onClick={() => void load()} disabled={loading}>{loading ? "Loading…" : "Search"}</button></div>
    {message ? <p className="marketplace-state" role="status">{message}</p> : null}
    <div className="marketplace-grid">{items.map((item) => <article className="marketplace-item" key={item.id}><img src={`${PUBLIC_API.replace(/\/$/, "")}${item.previewUrl}`} alt={`Generated theme preview for ${item.title}`} loading="lazy" /><div className="marketplace-item-copy"><p className="eyebrow">{item.category.replace("-", " ")}{item.authorName ? ` · ${item.authorName}` : " · Commander anonymous"}</p><h2>{item.title}</h2><p>{item.description}</p><time dateTime={item.createdAt}>{new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(item.createdAt))} · your local time zone</time><div className="marketplace-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div><button className="ud-main-btn" type="button" onClick={() => void downloadPack(item)}>Download pack</button></div></article>)}</div>
    {nextCursor ? <div className="marketplace-more"><button className="ud-main-btn" type="button" disabled={loading} onClick={() => void load(nextCursor, true)}>{loading ? "Loading…" : "Load more packs"}</button></div> : null}
  </div></section>;
}

interface ReceiptBar {
  label: string;
  value: number;
  valueLabel: string;
  note: string;
  target?: boolean;
}

function ReceiptChart({ title, subtitle, bars, scale }: { title: string; subtitle: string; bars: ReceiptBar[]; scale: number }) {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) { setActive(true); observer.disconnect(); }
    }, { threshold: 0.25 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!active) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setProgress(1); return; }
    let frame = 0;
    const started = performance.now();
    let lastUpdate = 0;
    const animate = (now: number) => {
      const progress = Math.min(1, (now - started) / 650);
      if (progress === 1 || now - lastUpdate >= 32) { lastUpdate = now; setProgress(1 - Math.pow(1 - progress, 3)); }
      if (progress < 1) frame = window.requestAnimationFrame(animate);
    };
    frame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frame);
  }, [active]);
  return <section ref={ref} className={`receipt-chart${active ? " is-active" : ""}`} aria-label={title}>
    <header><h3>{title}</h3><p>{subtitle}</p></header>
    <div className="receipt-bars" style={{ "--bar-count": bars.length } as CSSProperties}>
      {bars.map((bar) => {
        const match = bar.valueLabel.match(/^(\D*?)([\d,.]+)(.*)$/);
        const decimals = match?.[2]?.split(".")[1]?.length ?? 0;
        const count = active && match ? (bar.value * progress).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : match ? (0).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : "";
        return <article className={`receipt-bar-column${bar.target ? " is-target" : ""}`} key={bar.label}>
        <strong className="receipt-value" aria-label={bar.valueLabel}>{match ? `${match[1] ?? ""}${count}${match[3] ?? ""}` : bar.valueLabel}</strong>
        <div className="receipt-bar-well" role="meter" aria-label={`${bar.label}: ${bar.valueLabel}`} aria-valuemin={0} aria-valuemax={scale} aria-valuenow={active ? bar.value * progress : 0}>
          <span className="receipt-bar" style={{ height: `${Math.min(100, (bar.value / scale) * 100)}%` }} />
        </div>
        <strong className="receipt-bar-name">{bar.label}</strong><small>{bar.note}</small>
      </article>;
      })}
    </div>
  </section>;
}

function PerformanceBenchmarks() {
  return <section className="ud-section performance-receipts" aria-labelledby="performance-title">
    <div className="container">
      <div className="ud-section-title wow">
        <p className="eyebrow">The quick read · lower is better</p>
        <h2 id="performance-title" className="accent-heading">It's also just fast.</h2>
        <p>One measured EDCC launch and idle memory snapshots on the same PC. The 1.5-second mark is a target, not a competing-app result.</p>
      </div>
      <div className="receipt-chart-grid">
        <ReceiptChart title="Cold launch" subtitle="Launcher start to window creation." scale={5} bars={[
          { label: "EDCC", value: 4.731, valueLabel: "4.73 s", note: "One precise run" },
          { label: "1.5-second target", value: 1.5, valueLabel: "1.50 s", note: "Goal · not a measurement", target: true },
        ]} />
        <ReceiptChart title="Idle memory" subtitle="Five-minute idle snapshots, shown in MB." scale={1200} bars={[
          { label: "EDCC", value: 434, valueLabel: "434 MB", note: "414 MiB shell + core working set" },
          { label: "Chrome tabs", value: 831, valueLabel: "831 MB", note: "Four tab rows combined" },
          { label: "Chrome total", value: 1075, valueLabel: "1,075 MB", note: "All browser processes" },
        ]} />
      </div>
      <section className="benchmark-table-wrap" aria-labelledby="receipts-title">
        <h3 id="receipts-title">The receipts</h3>
        <p>One machine, one EDCC launch sample and the browser tabs you measured.</p>
        <div className="benchmark-table-scroll"><table className="benchmark-receipt-table"><thead><tr><th>Measured</th><th>EDCC</th><th>Four browser tabs</th><th>Chrome total</th></tr></thead><tbody>
          <tr><th scope="row">Window / idle sample</th><td data-label="EDCC">4.73 s launch</td><td data-label="Four browser tabs">5 minutes idle</td><td data-label="Chrome total">5 minutes idle</td></tr>
          <tr><th scope="row">Memory</th><td data-label="EDCC">337 MiB private · 414 MiB working set</td><td data-label="Four browser tabs">831 MB combined</td><td data-label="Chrome total">1,075 MB all processes</td></tr>
          <tr><th scope="row">CPU observed</th><td data-label="EDCC">0–0.2%</td><td data-label="Four browser tabs">Per-tab unavailable</td><td data-label="Chrome total">4–13%</td></tr>
        </tbody></table></div>
      </section>
      <div className="benchmark-receipts">
        <details>
          <summary>How this was tested</summary>
          <p>October 31, 2026 on a Ryzen 7 5700X, 24 GB DDR4-3200 MT/s, Windows 11. The last available precise desktop log run recorded 4,419 ms until the local API was ready and 4,731 ms until the window was created. This is one run, not a median or a controlled cold-start result.</p>
          <p>EDCC idle memory is one live sample: 337 MiB private bytes and 414 MiB working set across the shell and bundled core. Chrome Task Manager showed 831 MB across four tab rows and 1,075 MB across all browser processes after five minutes idle. These are separate sequential samples, and the memory columns are not identical metrics, so they provide context rather than a direct like-for-like score.</p>
          <p>The launcher health probe has since been changed to return as soon as it reads the healthy HTTP response. A new launch measurement is pending because the running app enforces single-instance behavior. This is an independent-project snapshot, not a lab benchmark; storage, journal size, browser cache, extensions and startup apps can move the numbers.</p>
          <p>EDCC's desktop launcher is Rust, and it starts a bundled Node core. The bars describe the whole app, not the launcher alone. The 1.5-second marker is a goal, not a competing app result.</p>
        </details>
        <details>
          <summary>Test machine specifications</summary>
          <dl className="benchmark-specs">
            <div><dt>Processor</dt><dd>AMD Ryzen 7 5700X</dd></div>
            <div><dt>Memory</dt><dd>24 GB DDR4-3200 MT/s</dd></div>
            <div><dt>Operating system</dt><dd>Windows 11</dd></div>
            <div><dt>CPU sample</dt><dd>EDCC 0–0.2%; Chrome 4–13% overall</dd></div>
          </dl>
        </details>
        <details>
          <summary>Chrome tab readings</summary>
          <dl className="benchmark-specs">
            <div><dt>Inara outfitting</dt><dd>179 MB</dd></div>
            <div><dt>EDSY ship builder</dt><dd>126 MB</dd></div>
            <div><dt>Spansh trade planner</dt><dd>119 MB</dd></div>
            <div><dt>EDSM galaxy map</dt><dd>407 MB</dd></div>
            <div><dt>CPU per tab</dt><dd>Not available in Task Manager</dd></div>
          </dl>
        </details>
      </div>
      <p className="performance-method">Values count up when the charts enter view. Bar wells have no backing panels; each chart uses one scale for its bars.</p>
    </div>
  </section>;
}

function HomePage() {
  const release = useReleaseInfo();
  const version = release?.version ?? FALLBACK_VERSION;
  const hangarArt = ships.flatMap((ship, index) => {
    const art = shipArtFor(ship.slug);
    return art ? [{ id: ship.slug, img: art.src, url: `/ships/${ship.slug}/`, height: [330, 420, 280, 390, 350][index % 5]!, title: ship.name, alt: `${ship.name}, ${ship.manufacturer ?? "Elite Dangerous"} ship` }] : [];
  });
  const [randomHangar] = useState(() => {
    const shuffled = [...hangarArt];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
    }
    return shuffled;
  });
  const featuredShips = randomHangar.slice(0, 6);
  return <div className="home-redesign">
    <section className="reference-hero" id="home">
      <div className="container reference-hero-content">
        <LetterHeading as="h1" className="reference-hero-title" text="It's all in one app." accentWord="app" />
        <p className="reference-hero-copy">An all-in-one desktop companion for your commander, fleet, builds, engineering, materials, exploration and trade, alongside the Elite tools you already use.</p>
        <div className="reference-hero-actions"><DownloadButton /><a href="#screenshots">Explore the cockpit</a></div>
        <p className="reference-status">Version {version} <span>·</span> journal stays local <span>·</span> built for Windows</p>
      </div>
      <HeroShipRibbon items={randomHangar} />
      <a className="reference-scroll-cue" href="#screenshots"><span>Scroll to explore</span><span aria-hidden="true">↓</span></a>
      <HomeMetrics />
    </section>

    <section className="toolbelt" aria-label="Elite Dangerous community tools"><div className="container"><p>One cockpit, alongside the tools you already trust.</p><div className="toolbelt-logos">
      <a href="https://inara.cz/elite/" target="_blank" rel="noreferrer"><img src="/brands/inara.png" alt="" /><span><strong>INARA</strong><small>Massive Elite database · ships, stations &amp; commanders</small></span></a>
      <a href="https://edsy.org/" target="_blank" rel="noreferrer"><img src="/brands/edsy.png" alt="" /><span><strong>EDSY</strong><small>Ship outfitting</small></span></a>
      <a href="https://www.edsm.net/" target="_blank" rel="noreferrer"><img src="/brands/edsm.svg" alt="" /><span><strong>EDSM</strong><small>Galaxy &amp; system data</small></span></a>
      <a href="https://spansh.co.uk/" target="_blank" rel="noreferrer"><img src="/brands/spansh.png" alt="" /><span><strong>SPANSH</strong><small>Route planning</small></span></a>
    </div><small>More community integrations are on the way.</small></div></section>

    <section className="reference-action" id="in-action"><div className="container">
        <header><LetterHeading text="See it in action" className="reference-section-title" /><p>A full-size capture from EDCC on Windows, with live commander stats in their actual screens.</p></header>
      <a className="reference-action-image" href="/app-shots/dashboard-current.png" target="_blank" rel="noreferrer"><img src="/app-shots/dashboard-current.png" alt="EDCC running on Windows, showing the full commander dashboard with fleet, materials, exploration and combat stats" width="1496" height="959" /></a>
      <a className="reference-image-link" href="/app-shots/dashboard-current.png" target="_blank" rel="noreferrer">Open the full dashboard image ↗</a>
    </div></section>

    <section className="reference-screens" id="screenshots" aria-labelledby="screens-heading"><div className="container reference-screens-title"><header><LetterHeading text="See it before you run it" className="reference-section-title" /><p>Full application screens, captured from EDCC. Keep scrolling to move through them.</p></header></div><AppScreenshotGallery /></section>

    <section className="reference-reviews" id="testimonials"><div className="container">
      <header><LetterHeading text="What people are (probably) saying" className="reference-section-title" /><p>Unsolicited, unverified, and mostly fictional. I did not pay for a single one of these.</p></header>
      <ReviewsMarquee items={mixedTestimonials} />
    </div></section>

    <section className="reference-themes" id="themes"><div className="container reference-theme-heading"><header><LetterHeading text="Skin it however you want" className="reference-section-title" /><p>Custom themes and full theme packs are a core feature: accents, light and dark appearances, transparent panels, custom backdrops, icons and shader wallpapers.</p></header><a href="/themes/">Theming docs ↗</a></div><ThemePacksSection /></section>

    <section className="reference-fleet"><div className="container"><header><LetterHeading text="Ships, in their proper colors" className="reference-section-title" /><p>A few hulls from the catalogue, with published stats and outfitting one click away.</p></header><MasonryGallery items={featuredShips} /><a className="reference-catalogue-button" href="/ships/">Browse the ship catalogue <span aria-hidden="true">↗</span></a></div></section>

    <PerformanceBenchmarks />

    <section className="reference-stack" id="features"><div className="container"><header><LetterHeading text="The useful bits, in their actual place." className="reference-section-title" /><p>One screen for the routine checks between jumps. Each card stays in view as the next part of your flight plan comes up.</p></header><div className="reference-stack-list">{features.map((item, index) => <article key={item.title} style={{ "--stack-index": index } as CSSProperties}><div><H3>{item.title}</H3><p>{item.detail}</p></div><a href={item.href} aria-label={`Learn about ${item.title}`}>↗</a></article>)}</div></div></section>

    <section className="reference-manifest" id="inside"><div className="container"><header><LetterHeading text="The manifest" className="reference-section-title" /><p>The useful bits, in their actual place. Here's what EDCC can do today; beta labels and missing integrations stay clearly marked.</p></header><div className="reference-manifest-grid">{inside.map((item, index) => <article key={item.title} className={`manifest-tile tile-${index + 1}`}><span aria-hidden="true">{item.title === "Ship catalogue" ? <img className="manifest-ship-icon" src="/ed-icons/ship-icon.svg" alt="" /> : <ReferenceIcon name={item.icon} />}</span><div><H3>{item.title}</H3><p>{item.detail}</p></div></article>)}</div><div className="reference-build-facts"><div><small>PLATFORM</small><strong>Windows 10 / 11</strong></div><div><small>DATA</small><strong>Elite journal, local-first</strong></div><div><small>PUBLIC API</small><strong>Community Goals + EDSM systems &amp; bodies</strong></div></div></div></section>

    <section className="reference-download" id="download"><div className="container"><div><h2>Take EDCC on your next flight.</h2><p>Windows installer, local journal data, no account.</p><p className="download-meta">{release ? <>{formatMegabytes(release.sizeBytes)} · SHA-256 <span title={release.sha256}>{shortHash(release.sha256)}</span> · built {formatReleaseDate(release.builtAt)}</> : "Release size and checksum appear here when a release is built."}</p></div><DownloadButton /></div></section>

  </div>;
}

function NotFoundPage() {
  return (
    <section className="page-width simple-page">
      <p className="eyebrow">404 / route not found</p>
      <h1>That page is not in the flight plan.</h1>
      <p>Check the navigation, or return to the EDCC home page.</p>
      <a className="ud-main-btn" href="/">
        Return to EDCC
      </a>
    </section>
  );
}

function TermsPage() {
  return (
    <section className="page-width reference-page terms-page">
      <header className="page-intro">
        <h1>Terms of Service</h1>
      </header>
      <section className="terms-privacy" aria-labelledby="terms-privacy-title">
        <h2 id="terms-privacy-title">Your journals stay local.</h2>
        <p>The EDCC app has no telemetry. The public website counts anonymous page visits, popular links and installer requests; it never receives commander or journal data.</p>
        <div className="terms-privacy-points">
          <article>
            <h3>Zero app telemetry</h3>
            <p>No usage stats, analytics SDKs or tracking IDs are sent from the EDCC desktop app.</p>
          </article>
          <article>
            <h3>Commander data stays local</h3>
            <p>Your journal history and settings live in the EDCC data folder on your own disk.</p>
          </article>
          <article>
            <h3>Anonymous site metrics</h3>
            <p>Daily visitors are estimated from a salted hash. Raw network addresses are not saved; Do Not Track is respected.</p>
          </article>
        </div>
        <p className="terms-privacy-note">Site metrics are retained for 90 days and can be disabled in this browser. <AnalyticsPrivacyToggle /></p>
        <p>Your app connects only to services you choose, such as Inara, EDSM or Spansh. <a href="/developers/#honesty">Read the data notes.</a></p>
      </section>
    </section>
  );
}

function PageLoading(props: { label: string }) {
  return (
    <div className="page-width page-loading" role="status" aria-live="polite">
      <Spinner size={30} />
      <span>{props.label}</span>
    </div>
  );
}

export function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  let active = path;
  let page: ReactNode;

  if (path === "/") {
    active = "/";
    page = <HomePage />;
  } else if (path === "/ships") {
    page = (
      <Suspense fallback={<PageLoading label="Loading the ship catalogue" />}>
        <ShipCataloguePage />
      </Suspense>
    );
  } else if (path === "/builds") {
    page = <CommunityBuildsPage />;
  } else if (path === "/builds/new") {
    active = "/builds";
    page = <Suspense fallback={<PageLoading label="Loading ship builder" />}><ShipBuildStudioPage /></Suspense>;
  } else if (path.startsWith("/builds/")) {
    const category = path.slice("/builds/".length);
    const buildCategory = COMMUNITY_BUILD_CATEGORIES.find((item) => item.value === category);
    if (buildCategory) {
      active = "/builds";
      page = <CommunityBuildsPage category={buildCategory.value} />;
    } else {
      active = "";
      page = <NotFoundPage />;
    }
  } else if (path.startsWith("/ships/")) {
    active = "/ships";
    page = <Suspense fallback={<PageLoading label="Loading ship specifications" />}><ShipDetailPage slug={decodeURIComponent(path.slice("/ships/".length))} /></Suspense>;
  } else if (path.startsWith("/build/")) {
    active = "";
    page = <PublicBuildPage id={path.slice("/build/".length)} />;
  } else if (path === "/releases") {
    page = (
      <Suspense fallback={<PageLoading label="Loading releases" />}>
        <ReleasesPage />
      </Suspense>
    );
  } else if (path === "/developers") {
    page = (
      <Suspense fallback={<PageLoading label="Loading the developer docs" />}>
        <DevelopersPage />
      </Suspense>
    );
  } else if (path === "/support") {
    page = (
      <Suspense fallback={<PageLoading label="Loading support options" />}>
        <SupportPage />
      </Suspense>
    );
  } else if (path === "/changelog") {
    page = (
      <Suspense fallback={<PageLoading label="Loading the changelog" />}>
        <ChangelogPage />
      </Suspense>
    );
  } else if (path === "/roadmap") {
    page = (
      <Suspense fallback={<PageLoading label="Loading the roadmap" />}>
        <RoadmapPage />
      </Suspense>
    );
  } else if (path === "/terms") {
    page = <TermsPage />;
  } else if (path === "/themes") {
    page = <ThemesGuidePage />;
  } else if (path === "/admin") {
    page = <Suspense fallback={<PageLoading label="Loading the release desk" />}><AdminPage /></Suspense>;
  } else if (path === "/marketplace") {
    page = <MarketplacePage />;
  } else {
    active = "";
    page = <NotFoundPage />;
  }

  return <SiteShell active={active}>{page}</SiteShell>;
}
